/**
 * In-app full debug console.
 *
 * Activated by entering "logtest" as the activation key on the device
 * activation screen, OR by setting `localStorage.zapp_debug_mode = "1"`.
 *
 * v1.3.18 — "proper full debug" rework:
 *   • Ring buffer expanded from 250 → 2000 entries so a complete
 *     playlist load (BEGIN → fetch → parse → cover-harvest → DONE)
 *     fits without earlier lines being evicted.
 *   • Per-entry payload bumped from 2 KB → 8 KB. Long stack traces,
 *     JSON dumps and HTTP error bodies are now visible in full.
 *   • Full URLs surfaced (no more `http://*****` masking) so we can
 *     see exactly which Plex cover / catch-up / EPG URL failed.
 *     Credentials in `username=…&password=…` query params are still
 *     redacted because those leak the user's IPTV subscription.
 *   • Boot buffer — every console.log/warn/error is captured from
 *     page load even before the user toggles debug on. When they
 *     activate via "logtest" the prior history is instantly visible.
 *   • Captures: window onerror, unhandledrejection, fetch failures,
 *     CapacitorHttp errors, and `<img>` onerror (so we can see
 *     exactly which cover/poster URL is broken).
 *   • Tagged events (`[loadPlaylist]`, `[cover-harvest]`,
 *     `[cover-proxy]`, `[xtream]`, `[fetch]`, `[img]`, `[error]`)
 *     so the overlay's filter box (added in DebugConsole.jsx)
 *     can scope the view.
 */

const STORAGE_KEY = "zapp_debug_mode";
const MAX_ENTRIES = 2000;
const MAX_MSG_LEN = 8000;

let _enabled = false;
let _installed = false;
const _entries = []; // [{ ts, level, tag, msg }]
const _listeners = new Set();

function loadFlag() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function saveFlag(on) {
  try {
    if (on) localStorage.setItem(STORAGE_KEY, "1");
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}

function emit() {
  const snap = _entries.slice();
  for (const fn of _listeners) {
    try {
      fn(snap, _enabled);
    } catch {
      /* listener crashes must not feed back into our hook */
    }
  }
}

// Strip ONLY credentials, not the URL itself — the path / host / query
// shape is exactly what we need to debug a cover-proxy or catch-up
// failure. Redacts `username=`, `password=`, `token=`, `Authorization`
// headers, and the `b2c://user:pass@host` userinfo form.
//
// v1.3.36 — also redact the provider host(s) listed below. These leak
// the user's IPTV subscription source when they share logs publicly,
// so even the host/path is masked.
const REDACT_HOSTS = [
  "m10.bitofza.today",
];
const CRED_PATTERNS = [
  [/([?&](?:username|password|token|api_key)=)[^&\s'"]+/gi, "$1***"],
  [/(Authorization:\s*Bearer\s+)[A-Za-z0-9._\-=]+/gi, "$1***"],
  [/(b2c:\/\/)[^@]+@/gi, "$1***:***@"],
];
function scrubCreds(s) {
  if (!s) return s;
  let out = s;
  for (const [re, sub] of CRED_PATTERNS) out = out.replace(re, sub);
  // Replace any full URL whose host matches the redact list with a
  // placeholder. Regex matches http(s):// up to the next whitespace,
  // quote or closing bracket.
  for (const host of REDACT_HOSTS) {
    const re = new RegExp(
      `https?:\\/\\/[^\\s'"<>)]*${host.replace(/[.]/g, "\\.")}[^\\s'"<>)]*`,
      "gi"
    );
    out = out.replace(re, "***redacted-provider-url***");
  }
  return out;
}

// Pull a leading "[tag]" out of the message so the overlay can colour
// / filter by subsystem. Falls back to the level when no tag is set.
function extractTag(msg, level) {
  const m = /^\s*\[([^\]]{1,40})\]/.exec(msg || "");
  return m ? m[1] : level;
}

function push(level, args) {
  let msg = "";
  try {
    msg = args
      .map((a) => {
        if (a == null) return String(a);
        if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack || ""}`;
        if (typeof a === "string") return a;
        try {
          return JSON.stringify(a);
        } catch {
          return String(a);
        }
      })
      .join(" ");
  } catch {
    msg = "<unserialisable>";
  }
  msg = scrubCreds(msg);
  if (msg.length > MAX_MSG_LEN) msg = msg.slice(0, MAX_MSG_LEN) + " …(truncated)";
  _entries.push({ ts: Date.now(), level, tag: extractTag(msg, level), msg });
  while (_entries.length > MAX_ENTRIES) _entries.shift();
  // v1.4.23 — persist to localStorage so a CRASH doesn't wipe the log.
  // Without this the user cannot recover the "last words" of a load
  // that died mid-parse on a Smart TV. We throttle to at most one
  // write every 500 ms + last 300 entries to cap the write cost.
  _scheduleCrashPersist();
  emit();
}

// v1.4.23 — Crash-survivor persistence layer.
//
// Writes the tail of the ring buffer (last 300 entries, plenty for
// post-crash forensics) to localStorage under `zapp_debug_crash_log`.
// Throttled to 500 ms so a chatty stream (M3U parser emits ~200
// entries per big playlist) doesn't hammer the storage layer.
//
// On next boot, `getCrashLog()` returns the tail of the previous
// session's log so the user can see EXACTLY what happened before the
// crash — even if they never had the chance to enable debug mode
// interactively.
const CRASH_LOG_KEY = "zapp_debug_crash_log";
const CRASH_LOG_TAIL = 300;
const CRASH_LOG_THROTTLE_MS = 500;
let _crashPersistTimer = null;
function _scheduleCrashPersist() {
  if (_crashPersistTimer) return;
  _crashPersistTimer = setTimeout(() => {
    _crashPersistTimer = null;
    try {
      const tail = _entries.slice(-CRASH_LOG_TAIL);
      const payload = {
        version: 1,
        savedAt: Date.now(),
        entries: tail.map((e) => ({
          ts: e.ts,
          level: e.level,
          tag: e.tag,
          msg: e.msg && e.msg.length > 800 ? e.msg.slice(0, 800) + "…" : e.msg,
        })),
      };
      localStorage.setItem(CRASH_LOG_KEY, JSON.stringify(payload));
    } catch {
      /* quota exceeded or storage unavailable — silent fail */
    }
  }, CRASH_LOG_THROTTLE_MS);
}

/** Returns the previous session's crash log tail (or null if none).
 *  Called by the DebugConsole boot banner to show "you crashed last
 *  time — here's what was happening". */
export function getCrashLog() {
  try {
    const raw = localStorage.getItem(CRASH_LOG_KEY);
    if (!raw) return null;
    const payload = JSON.parse(raw);
    return payload;
  } catch {
    return null;
  }
}

/** Clear the persisted crash log — user acknowledged it, or we're
 *  starting a fresh diagnostic session. */
export function clearCrashLog() {
  try { localStorage.removeItem(CRASH_LOG_KEY); } catch { /* ignore */ }
}

/** Install console hooks + global error catchers ONCE per page load.
 *  Runs unconditionally so we still buffer entries BEFORE the user
 *  enables debug mode — they're just not surfaced until enable. */
function install() {
  if (_installed || typeof window === "undefined") return;
  _installed = true;
  const orig = {
    log: console.log.bind(console),
    info: console.info?.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    debug: console.debug?.bind(console),
  };
  console.log = (...a) => { orig.log(...a); push("log", a); };
  if (orig.info) console.info = (...a) => { orig.info(...a); push("info", a); };
  if (orig.debug) console.debug = (...a) => { orig.debug(...a); push("log", a); };
  console.warn = (...a) => { orig.warn(...a); push("warn", a); };
  console.error = (...a) => { orig.error(...a); push("error", a); };

  window.addEventListener("error", (e) => {
    push("error", [
      `[error] window.onerror @ ${e.filename || "?"}:${e.lineno || "?"}:${e.colno || "?"} — ${e.message}`,
      e.error || "",
    ]);
  });
  window.addEventListener("unhandledrejection", (e) => {
    push("error", [`[error] unhandledrejection: ${e.reason?.message || e.reason}`]);
  });

  // v1.3.45 — Removed the global <img> error capture. With 590k-channel
  // catalogs every broken poster fired a `[img] failed to load: …` line
  // and these completely buried real warnings on the on-screen overlay.
  // Broken poster URLs are not actionable from a user's POV; broken
  // <video> tags / m3u8 manifests are surfaced through the player error
  // handler directly so we still see playback failures.

  // Wrap window.fetch so HTTP errors and exceptions are logged. Native
  // CapacitorHttp goes through a different path (it bypasses the
  // WebView fetch) — those are logged at the call site in imageCache.js.
  //
  // NOISE FILTER: known third-party telemetry / analytics endpoints
  // generate constant 30s-interval failures (cookie / consent gates)
  // that crowd out app-relevant entries. Skip them entirely.
  const NOISE_HOSTS = [
    "i.posthog.com",
    "us.i.posthog.com",
    "eu.i.posthog.com",
    "app.posthog.com",
    "us-assets.i.posthog.com",
    "google-analytics.com",
    "googletagmanager.com",
    "stats.g.doubleclick.net",
    "sentry.io",
    "ingest.sentry.io",
  ];
  function _isNoise(url) {
    if (typeof url !== "string" || !url) return false;
    return NOISE_HOSTS.some((h) => url.includes(`//${h}/`) || url.includes(`//${h}?`) || url.includes(`.${h}/`));
  }
  if (typeof window.fetch === "function" && !window.__zapp_fetch_wrapped__) {
    const origFetch = window.fetch.bind(window);
    window.__zapp_fetch_wrapped__ = true;
    window.fetch = async (input, init) => {
      const url = typeof input === "string" ? input : input?.url || "";
      const noisy = _isNoise(url);
      const t0 = Date.now();
      try {
        const r = await origFetch(input, init);
        if (!r.ok && !noisy) {
          push("warn", [`[fetch] HTTP ${r.status} ${r.statusText || ""} ${url} (${Date.now() - t0}ms)`]);
        }
        return r;
      } catch (err) {
        if (!noisy) {
          push("error", [`[fetch] threw: ${err?.message || err} url=${url} (${Date.now() - t0}ms)`]);
        }
        throw err;
      }
    };
  }
}

// ------------------------------------------------------------------ API

export function isDebugEnabled() {
  return _enabled;
}

export function getDebugEntries() {
  return _entries.slice();
}

export function subscribeToDebug(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

export function enableDebug() {
  saveFlag(true);
  _enabled = true;
  install();
  push("info", [`[debug] mode enabled — buffered ${_entries.length} prior entries`]);
  emit();
}

export function disableDebug() {
  saveFlag(false);
  _enabled = false;
  _entries.length = 0;
  emit();
}

export function clearDebug() {
  _entries.length = 0;
  emit();
}

/** Targeted log point — RECORDS even when debug mode is off (so we can
 *  scroll back through the boot history the instant the user turns
 *  debug on). The DebugConsole overlay just doesn't render until
 *  enabled. */
export function debugLog(...args) {
  push("log", args);
}

/** Best-effort export of all current entries as a single newline-joined
 *  string — used by DebugConsole's "Copy all" button. */
export function dumpDebugText() {
  return _entries
    .map((e) => `${new Date(e.ts).toISOString()} [${e.level}] ${e.msg}`)
    .join("\n");
}

// Boot — always install hooks so pre-enable history is captured.
_enabled = loadFlag();
install();
if (_enabled) {
  push("info", [`[debug] mode restored from previous session`]);
}

// v1.4.77 — Removed the "auto-enable on prior crash log" block.
//
// Previously any log entry persisted by `_scheduleCrashPersist` (which
// runs on EVERY console.log — so effectively always) would flip debug
// mode ON the next time the app booted, causing the on-screen console
// to appear on fresh installs or after a normal quit. That surprised
// users who never opted in.
//
// The crash log is still written and remains available via
// `getCrashLog()` for the "Show last session's log" button in
// PlayerSettings — but debug mode itself now only turns on when the
// user explicitly enables it in-app (PlayerSettings toggle or
// activation-key "logtest") or when the admin panel force-enables it
// on a device via the remote-diagnostics push.
